import styled from 'styled-components';
import { ComponentOptions } from 'menus/optionsMenu/component/ComponentOptions';
import { ReferenceOptions } from 'menus/optionsMenu/reference/ReferenceOptions';
import { WorkspaceOptions } from 'menus/optionsMenu/workspace/WorkspaceOptions';
import { TAB_LIST_SELECTOR, Tab, TabList } from '@ardoq/tabs';
import { Box } from '@ardoq/layout';
import { OptionsMenuState, optionsMenu$, switchTab } from './optionsMenu$';
import { dispatchAction, connect } from '@ardoq/rxbeach';
import { trackTabClicked } from '../tracking';
import { combineLatest, map } from 'rxjs';
import { t, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import { isTruthy } from '@ardoq/common-helpers';
import { isFocusedChrome$ } from 'scope/focusedChrome$';

const Wrapper = styled.div`
  width: 0;
  min-width: 100%;
  display: flex;
  flex-direction: column;
  background-color: white;
  ${TAB_LIST_SELECTOR} {
    height: inherit;
  }
`;

type OptionsMenuProps = OptionsMenuState & {
  i18nLocale: I18nLocale;
  isViewpointMode: boolean;
  isFocusedChrome: boolean;
};

const OptionsMenuBase = ({
  tabId,
  i18nLocale,
  isViewpointMode,
  isFocusedChrome,
}: OptionsMenuProps) => {
  const tabs = [
    !isFocusedChrome && {
      tabId: 'workspace',
      label: t(i18nLocale, 'menus:optionsMenu.tabs.workspace', 'Workspace'),
      content: <WorkspaceOptions />,
    },
    {
      tabId: 'component',
      label: t(i18nLocale, 'menus:optionsMenu.tabs.component', 'Component'),
      content: <ComponentOptions />,
    },
    {
      tabId: 'reference',
      label: t(i18nLocale, 'menus:optionsMenu.tabs.reference', 'Reference'),
      content: <ReferenceOptions />,
    },
  ].filter(isTruthy);

  const activeTab = tabs.find(tab => tab.tabId === tabId) ?? tabs[0];

  return (
    <Wrapper>
      <Box
        padding="xsmall"
        borderWidthBottom={1}
        borderColor="borderAction"
        backgroundColor="surfaceDefault"
      >
        <TabList
          onTabChange={tabId => {
            trackTabClicked(tabId, isViewpointMode);
            dispatchAction(switchTab(tabId));
          }}
          activeTabId={tabId}
          overflowType="chevrons"
          moreButtonLabel={t(i18nLocale, 'common:labels.more', 'More')}
        >
          {tabs.map(tab => (
            <Tab key={tab.tabId} tabId={tab.tabId} label={tab.label} />
          ))}
        </TabList>
      </Box>
      <Box overflow="auto">{activeTab.content}</Box>
    </Wrapper>
  );
};

const optionsMenuViewModel$ = combineLatest([
  optionsMenu$,
  i18nLocale$,
  loadedGraph$,
  isFocusedChrome$,
]).pipe(
  map(([state, i18nLocale, { isViewpointMode }, isFocusedChrome]) => ({
    ...state,
    i18nLocale,
    isViewpointMode,
    isFocusedChrome,
  }))
);

export const OptionsMenu = connect(OptionsMenuBase, optionsMenuViewModel$);
